import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Search } from "lucide-react";
import { Reveal } from "./ui";
import { faqIntro, faqCategories } from "./faqData";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40);

const Email = () => (
  <a href="mailto:info@thesashbag.com" className="underline font-semibold" style={{ color: "var(--tan-deep)" }} data-testid="faq-email">info@thesashbag.com</a>
);

const Para = ({ text }) => {
  const parts = text.split("info@thesashbag.com");
  return (
    <p className="text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
      {parts.map((p, i) => (
        <span key={i}>{p}{i < parts.length - 1 && <Email />}</span>
      ))}
    </p>
  );
};

const Block = ({ block }) =>
  typeof block === "string" ? (
    <Para text={block} />
  ) : (
    <ul className="list-disc pl-5 space-y-1 text-[1.02rem] text-[var(--ink-soft)]">
      {block.list.map((b, i) => (<li key={i}>{b}</li>))}
    </ul>
  );

const Item = ({ item, open, onToggle, id }) => (
  <div className="border-b border-[var(--line)]">
    <button onClick={onToggle} className="w-full flex items-start justify-between gap-4 text-left py-5" data-testid={`faq-q-${id}`} aria-expanded={open}>
      <span className="sash-serif text-lg md:text-xl leading-snug text-[var(--ink)]">{item.q}</span>
      <span className="flex-shrink-0 mt-1 text-[var(--tan-deep)]">{open ? <Minus size={20} /> : <Plus size={20} />}</span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          style={{ overflow: "hidden" }}
          data-testid={`faq-a-${id}`}
        >
          <div className="pb-6 md:pr-10 space-y-3">
            {item.a.map((block, i) => (<Block key={i} block={block} />))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Faq = () => {
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const itemText = (item) =>
    [item.q, ...item.a.map((b) => (typeof b === "string" ? b : b.list.join(" ")))].join(" ").toLowerCase();

  const cats = q
    ? faqCategories
        .map((c) => ({ ...c, items: c.items.filter((it) => itemText(it).includes(q)) }))
        .filter((c) => c.items.length)
    : faqCategories;

  const totalMatches = cats.reduce((n, c) => n + c.items.length, 0);

  return (
    <section id="faq" className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-[var(--line)]" data-testid="faq-section">
      <div className="max-w-[880px] mx-auto">
        <Reveal>
          <div className="text-center">
            <span className="eyebrow">FAQ</span>
            <h2 className="mt-4 text-[clamp(2rem,4.5vw,3.2rem)] leading-[1.04]">
              Everything you need to <span className="italic" style={{ color: "var(--clay)" }}>know.</span>
            </h2>
            <p className="mt-3 text-[1.05rem] font-semibold text-[var(--ink)]">
              The website, orders, pre-orders &amp; what's next.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.04}>
          <div className="mt-8 relative max-w-[520px] mx-auto">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tan-deep)]" />
            <input
              className="sf-input pl-11"
              placeholder="Search the FAQ… (orders, refunds, Kickstarter, rewards)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="faq-search"
            />
          </div>
        </Reveal>

        {q && (
          <p className="mt-4 text-center text-sm text-[var(--ink-soft)]" data-testid="faq-search-count">
            {totalMatches === 0 ? "No matches — try another word or email us below." : `${totalMatches} matching question${totalMatches > 1 ? "s" : ""}`}
          </p>
        )}

        {!q && (
          <Reveal delay={0.05}>
            <div className="mt-8 space-y-4 max-w-[46rem] mx-auto">
              {faqIntro.map((p, i) => (<Para key={i} text={p} />))}
            </div>
          </Reveal>
        )}

        {cats.map((cat, ci) => (
          <Reveal key={cat.title} delay={0.03}>
            <div className="mt-12">
              <h3 className="eyebrow pb-2 border-b-2 border-[var(--tan)]" style={{ color: "var(--tan-deep)" }}>{cat.title}</h3>
              <div className="mt-1">
                {cat.items.map((it) => {
                  const id = slug(it.q);
                  const isOpen = q ? true : openId === id;
                  return (
                    <Item key={id} id={id} item={it} open={isOpen} onToggle={() => setOpenId(openId === id ? null : id)} />
                  );
                })}
              </div>
            </div>
          </Reveal>
        ))}

        <Reveal>
          <p className="mt-12 text-center text-[var(--ink-soft)]">
            Still need help? Reach Customer Service at <Email />.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
